import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FiEdit2, FiPhone, FiPlus, FiTrash2 } from 'react-icons/fi';
import { ValidationError } from 'yup';
import { FieldShell } from './fields';
import { maskPhone } from './masks';
import { ContactItem, contactSchema, newKey } from './personForm';
import * as S from './styles';

const emptyContact = (): ContactItem => ({
  key: newKey(),
  name_1: '',
  phone: '',
  cell_phone: '',
});

const hasContent = (contact: ContactItem) =>
  [contact.name_1, contact.phone, contact.cell_phone].some((value) => value.trim() !== '');

interface Props {
  items: ContactItem[];
  onChange: (items: ContactItem[]) => void;
  onPendingChange: (pending: boolean) => void;
  error?: string;
  disabled?: boolean;
}

export function ContactSection({ items, onChange, onPendingChange, error, disabled }: Props) {
  const [draft, setDraft] = useState<ContactItem | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const isEditing = Boolean(draft && items.some((item) => item.key === draft.key));

  useEffect(() => {
    onPendingChange(Boolean(draft && hasContent(draft)));
  }, [draft, onPendingChange]);

  const openEditor = (contact?: ContactItem) => {
    setDraft(contact ? { ...contact } : emptyContact());
    setErrors({});
  };

  const closeEditor = () => {
    setDraft(null);
    setErrors({});
  };

  const update = (field: keyof ContactItem, value: string) => {
    setDraft((current) => (current ? { ...current, [field]: value } : current));
    // Preencher qualquer telefone resolve o aviso "informe um telefone ou celular".
    setErrors(({ [field]: _removed, ...rest }) =>
      field === 'cell_phone' || field === 'phone' ? { name_1: rest.name_1 } : rest,
    );
  };

  const confirm = () => {
    if (!draft) return;
    try {
      contactSchema.validateSync(draft, { abortEarly: false });
    } catch (validation) {
      const next: Record<string, string> = {};
      (validation as ValidationError).inner.forEach((issue) => {
        if (issue.path && !next[issue.path]) next[issue.path] = issue.message;
      });
      setErrors(next);
      return;
    }

    onChange(
      isEditing
        ? items.map((item) => (item.key === draft.key ? draft : item))
        : [...items, draft],
    );
    closeEditor();
  };

  const remove = (key: string) => {
    onChange(items.filter((item) => item.key !== key));
    if (draft?.key === key) closeEditor();
  };

  return (
    <S.Card id="secao-contatos" aria-labelledby="titulo-contatos">
      <S.CardTitle id="titulo-contatos">
        <span>
          Contatos<small>{items.length > 0 ? `${items.length} cadastrado(s)` : 'opcional'}</small>
        </span>
      </S.CardTitle>

      {error && (
        <S.Banner $tone="error" role="alert" style={{ marginBottom: 10 }}>
          {error}
        </S.Banner>
      )}

      {items.length === 0 && !draft && <S.Empty>Nenhum contato adicionado.</S.Empty>}

      {items.length > 0 && (
        <S.ItemList>
          {items.map((item) => (
            <S.Item key={item.key} $editing={draft?.key === item.key} data-testid="contact-item">
              <div>
                <strong>{item.name_1}</strong>
                <span>
                  {[item.cell_phone && `Cel. ${item.cell_phone}`, item.phone && `Tel. ${item.phone}`]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </div>
              <S.ItemActions>
                <button
                  type="button"
                  title="Editar contato"
                  aria-label={`Editar contato ${item.name_1}`}
                  onClick={() => openEditor(item)}
                  disabled={disabled}
                >
                  <FiEdit2 size={14} />
                </button>
                <button
                  type="button"
                  className="danger"
                  title="Remover contato"
                  aria-label={`Remover contato ${item.name_1}`}
                  onClick={() => remove(item.key)}
                  disabled={disabled}
                >
                  <FiTrash2 size={14} />
                </button>
              </S.ItemActions>
            </S.Item>
          ))}
        </S.ItemList>
      )}

      {draft ? (
        <S.Editor
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              confirm();
            }
          }}
        >
          <S.CardTitle as="div" style={{ fontSize: 13 }}>
            <span>
              <FiPhone size={13} style={{ marginRight: 6 }} />
              {isEditing ? 'Editar contato' : 'Novo contato'}
            </span>
          </S.CardTitle>
          <S.Grid $cols={3}>
            <FieldShell label="Nome do contato" required error={errors.name_1}>
              <input
                aria-label="Nome do contato"
                placeholder="Ex.: Maria (financeiro)"
                value={draft.name_1}
                onChange={(e) => update('name_1', e.target.value)}
                autoFocus
              />
            </FieldShell>
            <FieldShell label="Celular" error={errors.cell_phone}>
              <input
                aria-label="Celular"
                inputMode="tel"
                placeholder="(00) 00000-0000"
                value={draft.cell_phone}
                onChange={(e) => update('cell_phone', maskPhone(e.target.value))}
              />
            </FieldShell>
            <FieldShell label="Telefone" error={errors.phone}>
              <input
                aria-label="Telefone"
                inputMode="tel"
                placeholder="(00) 0000-0000"
                value={draft.phone}
                onChange={(e) => update('phone', maskPhone(e.target.value))}
              />
            </FieldShell>
          </S.Grid>
          <S.EditorActions>
            <Button variant="light" size="sm" onClick={closeEditor}>
              Descartar
            </Button>
            <Button variant="primary" size="sm" onClick={confirm}>
              {isEditing ? 'Atualizar contato' : 'Adicionar contato'}
            </Button>
          </S.EditorActions>
        </S.Editor>
      ) : (
        <S.AddButton type="button" onClick={() => openEditor()} disabled={disabled}>
          <FiPlus size={14} /> Adicionar contato
        </S.AddButton>
      )}
    </S.Card>
  );
}
